import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import api from '../lib/api';
import { gbp, catOf } from '../lib/shop';

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(''); setResults([]); }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/search/suggest', { params: { q } });
        setResults(data);
      } catch {} finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(timer.current);
  }, [q]);

  if (!open) return null;

  const go = (slug) => { onClose(); navigate(`/product/${slug}`); };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="search-overlay">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute top-0 left-0 right-0 bg-[#FAFAF8] border-b hairline shadow-2xl">
        <div className="max-w-3xl mx-auto p-4 sm:p-6">
          <div className="flex items-center gap-3 border-b-2 border-[#0B0F12] pb-3">
            <Search className="w-5 h-5 text-[#8A8F95]" />
            <input ref={inputRef} data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search compound, strength, format or SKU…"
              className="flex-1 bg-transparent text-lg focus:outline-none placeholder:text-[#B7BCC1]" />
            <button data-testid="search-close" onClick={onClose} className="p-1"><X className="w-5 h-5" /></button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading && <p className="py-6 font-mono2 text-xs uppercase tracking-widest text-[#8A8F95]">Searching…</p>}
            {!loading && q.trim().length >= 2 && results.length === 0 && (
              <p data-testid="search-no-results" className="py-6 text-sm text-[#575B60]">No matches for “{q}”. Try a compound name, strength or format.</p>
            )}
            {results.map((r) => (
              <button key={r.id} data-testid={`search-result-${r.slug}`} onClick={() => go(r.slug)}
                className="w-full flex items-center gap-4 py-3 border-b hairline hover:bg-[#F4F4F0] transition-colors text-left px-2">
                <img src={r.image} alt="" className="w-12 h-12 object-cover bg-[#F4F4F0]" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold uppercase tracking-wide truncate">{r.name} <span className="text-[#8A8F95]">{r.strength}</span></p>
                  <p className="font-mono2 text-[10px] uppercase tracking-wider" style={{ color: catOf(r.category).color }}>
                    {catOf(r.category).name} · {r.format}
                  </p>
                </div>
                <span className="font-mono2 text-sm font-semibold">{gbp(r.price)}</span>
              </button>
            ))}
          </div>
          <p className="pt-3 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">ESC to close · CMD/CTRL+K to open</p>
        </div>
      </div>
    </div>
  );
}
